import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Clock3,
  CheckCircle2,
  ChevronRight,
  HelpCircle,
  Link2,
  Mail,
  Phone,
  ShieldCheck,
  User
} from 'lucide-react';
import { ProductService } from '../../services/productService';
import { TopupTemplateService } from '../../services/topupTemplateService';
import { CategoryService } from '../../services/categoryService';
import { TopupOrderService } from '../../services/topupOrderService';
import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';
import { useAuth } from '../../contexts/AuthContext';

const formatCurrency = (amount) => {
  const numericAmount = Number(amount || 0);
  return `${new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(numericAmount)} DA`;
};

const normalizeOfferText = (value) => String(value || '').trim();

const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const LEGACY_PRODUCT_UNIT_MAP = [
  { keywords: ['pubg', 'uc'], unit: 'UC' },
  { keywords: ['free fire', 'diamant', 'diamond'], unit: 'Diamants' },
  { keywords: ['tiktok', 'coins', 'coin'], unit: 'Coins' },
  { keywords: ['instagram', 'followers', 'follower', 'abonnes', 'abonne'], unit: 'Followers' },
  { keywords: ['facebook', 'followers', 'follower'], unit: 'Followers' }
];

const inferLegacyProductUnit = (product, offer) => {
  const haystack = [
    product?.game,
    product?.name,
    product?.description,
    offer?.name,
    offer?.value
  ]
    .map((entry) => normalizeOfferText(entry).toLowerCase())
    .filter(Boolean)
    .join(' ');

  const matchedEntry = LEGACY_PRODUCT_UNIT_MAP.find((entry) =>
    entry.keywords.some((keyword) => haystack.includes(keyword))
  );

  if (matchedEntry) {
    return matchedEntry.unit;
  }

  const trailingUnitMatch = normalizeOfferText(offer?.value || offer?.name).match(
    /([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s]{1,24})$/u
  );

  return trailingUnitMatch ? trailingUnitMatch[1].trim() : '';
};

const splitOfferValueAndUnit = (text, fallbackUnit = '') => {
  const normalizedText = normalizeOfferText(text);
  if (!normalizedText) {
    return {
      primary: '',
      unit: fallbackUnit
    };
  }

  if (fallbackUnit) {
    const fallbackPattern = new RegExp(`^(.*?)(?:\\s+)?(${escapeRegExp(fallbackUnit)})$`, 'i');
    const fallbackMatch = normalizedText.match(fallbackPattern);
    if (fallbackMatch) {
      return {
        primary: normalizeOfferText(fallbackMatch[1]) || normalizedText,
        unit: fallbackUnit
      };
    }
  }

  const genericMatch = normalizedText.match(/^(.*?)(?:\s+)([A-Za-zÀ-ÿ][A-Za-zÀ-ÿ\s]{1,24})$/u);
  if (genericMatch && !/\d/.test(genericMatch[2])) {
    return {
      primary: normalizeOfferText(genericMatch[1]) || normalizedText,
      unit: normalizeOfferText(genericMatch[2])
    };
  }

  return {
    primary: normalizedText,
    unit: fallbackUnit
  };
};

const resolveTopupDetailImage = (product) => {
  return product?.detailImageUrl || product?.imageUrl || product?.coverImageUrl || '';
};

const hasStructuredTopupConfig = (product = {}) => {
  return Boolean(
    String(product?.topupTemplateId || '').trim() ||
    String(product?.topupTemplateName || '').trim() ||
    String(product?.topupTemplateFamily || '').trim() ||
    String(product?.topupFulfillmentMode || '').trim()
  );
};

const resolveTopupUnitIcon = (product) => {
  return String(product?.unitIconUrl || '').trim();
};

const getOfferDisplay = (product, offer) => {
  const rawName = normalizeOfferText(offer?.name);
  const rawValue = normalizeOfferText(offer?.value);
  const configuredUnit = normalizeOfferText(offer?.unit);
  const fallbackUnit = configuredUnit || inferLegacyProductUnit(product, offer);
  const splitValue = splitOfferValueAndUnit(rawValue || rawName, fallbackUnit);
  const fullLabel = [splitValue.primary, splitValue.unit].filter(Boolean).join(' ').trim() || rawValue || rawName || 'Offre';

  return {
    primary: splitValue.primary || rawValue || rawName || 'Offre',
    unit: splitValue.unit || fallbackUnit,
    fullLabel,
    secondary:
      rawName && rawValue && rawName.toLowerCase() !== rawValue.toLowerCase()
        ? rawName
        : 'Recharge instantanee'
  };
};

const getFieldIcon = (type) => {
  if (type === TopupTemplateService.FIELD_TYPES.INSTRUCTION) return HelpCircle;
  if (type === TopupTemplateService.FIELD_TYPES.PHONE) return Phone;
  if (type === TopupTemplateService.FIELD_TYPES.URL) return Link2;
  if (type === TopupTemplateService.FIELD_TYPES.EMAIL) return Mail;
  return User;
};

const getInputType = (type) => {
  if (type === TopupTemplateService.FIELD_TYPES.PHONE) return 'tel';
  if (type === TopupTemplateService.FIELD_TYPES.EMAIL) return 'email';
  if (type === TopupTemplateService.FIELD_TYPES.NUMBER) return 'number';
  if (type === TopupTemplateService.FIELD_TYPES.URL) return 'url';
  return 'text';
};

const TopupServiceDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [product, setProduct] = useState(null);
  const [template, setTemplate] = useState(null);
  const [selectedOfferId, setSelectedOfferId] = useState('');
  const [formValues, setFormValues] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const handleUniversalBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate('/topup');
  };

  useEffect(() => {
    const loadService = async () => {
      try {
        setLoading(true);
        const productData = await ProductService.getProductById(productId);

        if (!productData || productData.status !== 'active') {
          throw new Error('Service Topup introuvable');
        }

        const activeCategories = await CategoryService.getActiveCategories();
        if (ProductService.resolveProductSection(productData, activeCategories) !== CategoryService.CATEGORY_SECTIONS.TOPUP) {
          throw new Error('Ce service n est pas un service Topup');
        }

        let templateData = null;
        if (productData.topupTemplateId) {
          templateData = await TopupTemplateService.getTemplateById(productData.topupTemplateId);
        }

        setProduct(productData);
        setTemplate(templateData);
        setSelectedOfferId(productData.offers?.[0]?.id || '');

        const initialValues = {};
        (templateData?.fields || []).forEach((field) => {
          initialValues[field.id] = '';
        });
        setFormValues(initialValues);
      } catch (error) {
        console.error('Erreur lors du chargement du service Topup:', error);
        addNotification('error', error.message || 'Impossible de charger le service Topup');
        navigate('/topup');
      } finally {
        setLoading(false);
      }
    };

    loadService();
  }, [addNotification, navigate, productId]);

  const templateFields = useMemo(() => {
    if (template) {
      return template.fields;
    }

    if (hasStructuredTopupConfig(product)) {
      return [];
    }

    return [
      {
        id: 'player_id',
        label: 'Entrez votre ID joueur',
        type: TopupTemplateService.FIELD_TYPES.TEXT,
        required: true,
        placeholder: 'Ex: 5123456789',
        helpText: 'Comment trouver mon ID ?'
      }
    ];
  }, [product, template]);

  const selectedOffer = useMemo(() => {
    return product?.offers?.find((offer) => offer.id === selectedOfferId) || product?.offers?.[0] || null;
  }, [product, selectedOfferId]);

  const updateFieldValue = (fieldId, value) => {
    setFormValues((current) => ({ ...current, [fieldId]: value }));
    if (fieldErrors[fieldId]) {
      setFieldErrors((current) => ({ ...current, [fieldId]: null }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    templateFields.forEach((field) => {
      if (field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION) {
        return;
      }

      if (field.required && !String(formValues[field.id] || '').trim()) {
        nextErrors[field.id] = 'Ce champ est requis';
      }
    });

    if (!selectedOffer) {
      nextErrors.offer = 'Selectionnez un montant';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const currentSubmissionMode = useMemo(() => {
    return (
      template?.submissionMode ||
      product?.topupFulfillmentMode ||
      TopupTemplateService.SUBMISSION_MODES.DASHBOARD
    );
  }, [product, template]);

  const buildWhatsappMessage = (orderId = '') => {
    const lines = [
      `Bonjour, je veux passer une commande Topup.`,
      ``,
      `Service: ${product?.game || product?.name || 'Topup'}`,
      `Offre: ${getOfferDisplay(product, selectedOffer).fullLabel}`,
      `Prix: ${formatCurrency(selectedOffer?.price || 0)}`
    ];

    if (orderId) {
      lines.push(`Reference: ${orderId}`);
    }

    templateFields.forEach((field) => {
      if (field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION) {
        return;
      }

      const value = String(formValues[field.id] || '').trim();
      if (value) {
        lines.push(`${field.label}: ${value}`);
      }
    });

    if (userProfile?.displayName || userProfile?.email) {
      lines.push(`Revendeur: ${userProfile?.displayName || userProfile?.email}`);
    }

    return encodeURIComponent(lines.join('\n'));
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      addNotification('error', 'Veuillez completer les informations demandees');
      return;
    }

    setSubmitting(true);

    try {
      if (!currentUser?.uid) {
        throw new Error('Utilisateur non authentifie');
      }

      const submissionResult = await TopupOrderService.createOrder({
        userId: currentUser.uid,
        userProfile,
        product,
        template,
        offer: selectedOffer,
        formValues,
        templateFields,
        notificationChannels: [TopupOrderService.NOTIFICATION_CHANNELS.IN_APP]
      });
      const orderId = submissionResult?.orderId || '';
      const nextBalance = Number(submissionResult?.newBalance);
      const balanceLabel = Number.isFinite(nextBalance)
        ? ` Nouveau solde: ${formatCurrency(nextBalance)}`
        : '';

      if (
        currentSubmissionMode === TopupTemplateService.SUBMISSION_MODES.WHATSAPP &&
        template?.whatsappNumber
      ) {
        const phoneNumber = String(template.whatsappNumber).replace(/[^\d]/g, '');
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${buildWhatsappMessage(orderId)}`;
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
        addNotification('success', `Commande Topup enregistree, solde debite.${balanceLabel}`);
      } else {
        addNotification('success', `Commande Topup envoyee au dashboard admin et solde debite.${balanceLabel}`);
      }
    } catch (error) {
      console.error('Erreur lors de la creation de la commande Topup:', error);
      addNotification('error', error.message || 'Impossible de creer la commande Topup');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  const unitIconUrl = resolveTopupUnitIcon(product);

  return (
    <div className="mx-auto max-w-6xl px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="space-y-6">
        <div className="rounded-[30px] border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          <button
            type="button"
            onClick={handleUniversalBack}
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-800"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour
          </button>

          <div className="mt-5 flex flex-col gap-5 md:flex-row md:items-start">
            <div className="flex gap-4">
              <div className="h-24 w-24 shrink-0 overflow-hidden rounded-[24px] bg-slate-100 sm:h-28 sm:w-28">
                {resolveTopupDetailImage(product) ? (
                  <img
                    src={resolveTopupDetailImage(product)}
                    alt={product.name}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-600 text-xl font-bold text-white">
                    {(product.game || product.name || 'T').charAt(0)}
                  </div>
                )}
              </div>

              <div className="min-w-0">
                <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                  {product.game || product.name}
                </h1>
                <div className="mt-3 inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-600">
                  {product.category || 'Topup'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {templateFields.length > 0 ? (
            <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
              <div className="space-y-4">
                {templateFields.map((field) => {
                  const Icon = getFieldIcon(field.type);

                  return (
                    <div key={field.id} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-4">
                      <label className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-900">
                        <span>{field.label}</span>
                        {field.helpText ? <HelpCircle className="h-4 w-4 text-slate-400" /> : null}
                      </label>

                      {field.type === TopupTemplateService.FIELD_TYPES.INSTRUCTION ? (
                        <div className="rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-sm leading-6 text-slate-700">
                          {field.helpText}
                        </div>
                      ) : field.type === TopupTemplateService.FIELD_TYPES.TEXTAREA ? (
                        <textarea
                          rows={4}
                          value={formValues[field.id] || ''}
                          onChange={(event) => updateFieldValue(field.id, event.target.value)}
                          placeholder={field.placeholder || ''}
                          className={`w-full rounded-2xl border bg-white px-4 py-3 text-base text-slate-800 outline-none transition focus:ring-4 ${
                            fieldErrors[field.id]
                              ? 'border-red-300 focus:border-red-300 focus:ring-red-100'
                              : 'border-slate-200 focus:border-blue-400 focus:ring-blue-50'
                          }`}
                        />
                      ) : field.type === TopupTemplateService.FIELD_TYPES.SELECT ? (
                        <select
                          value={formValues[field.id] || ''}
                          onChange={(event) => updateFieldValue(field.id, event.target.value)}
                          className={`w-full rounded-2xl border bg-white px-4 py-3 text-base text-slate-800 outline-none transition focus:ring-4 ${
                            fieldErrors[field.id]
                              ? 'border-red-300 focus:border-red-300 focus:ring-red-100'
                              : 'border-slate-200 focus:border-blue-400 focus:ring-blue-50'
                          }`}
                        >
                          <option value="">Selectionner</option>
                          {(field.options || []).map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      ) : (
                        <div className={`flex items-center gap-3 rounded-2xl border bg-white px-4 py-3 transition focus-within:ring-4 ${
                          fieldErrors[field.id]
                            ? 'border-red-300 focus-within:border-red-300 focus-within:ring-red-100'
                            : 'border-slate-200 focus-within:border-blue-400 focus-within:ring-blue-50'
                        }`}>
                          <input
                            type={getInputType(field.type)}
                            value={formValues[field.id] || ''}
                            onChange={(event) => updateFieldValue(field.id, event.target.value)}
                            placeholder={field.placeholder || ''}
                            className="min-w-0 flex-1 border-0 bg-transparent text-base text-slate-800 outline-none placeholder:text-slate-400"
                          />
                          <Icon className="h-5 w-5 shrink-0 text-blue-500" />
                        </div>
                      )}

                      {field.helpText && field.type !== TopupTemplateService.FIELD_TYPES.INSTRUCTION ? (
                        <div className="mt-3 flex items-center justify-between text-sm text-slate-500">
                          <span>{field.helpText}</span>
                          <ChevronRight className="h-4 w-4" />
                        </div>
                      ) : null}

                      {fieldErrors[field.id] ? (
                        <p className="mt-2 text-sm text-red-600">{fieldErrors[field.id]}</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-col gap-3">
              <div>
                <h2 className="text-xl font-bold text-slate-900 sm:text-2xl">Choisissez le montant</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Selectionnez l offre qui correspond a la commande.
                </p>
              </div>
              {selectedOffer ? (
                <div className="inline-flex w-fit items-center self-start rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700">
                  Offre choisie: {getOfferDisplay(product, selectedOffer).fullLabel}
                </div>
              ) : null}
            </div>

            <div className="mt-5 grid grid-cols-2 gap-2.5 sm:gap-3 lg:grid-cols-4">
              {(product.offers || []).map((offer) => {
                const isSelected = selectedOfferId === offer.id;
                const offerDisplay = getOfferDisplay(product, offer);

                return (
                  <button
                    key={offer.id}
                    type="button"
                    onClick={() => setSelectedOfferId(offer.id)}
                    className={`relative min-h-[118px] rounded-2xl border bg-white p-3 text-left transition-all duration-200 sm:min-h-[126px] ${
                      isSelected
                        ? 'border-blue-500 shadow-[0_0_0_3px_rgba(59,130,246,0.10)]'
                        : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
                    }`}
                  >
                    {isSelected ? (
                      <span className="absolute right-2.5 top-2.5 flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 text-white shadow-sm sm:right-3 sm:top-3">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                      </span>
                    ) : null}

                    <div className="flex min-h-[86px] items-start gap-2 pr-6 sm:min-h-[88px] sm:gap-2.5 sm:pr-7">
                      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-[#f7f5ef] sm:h-10 sm:w-10">
                        {unitIconUrl ? (
                          <img
                            src={unitIconUrl}
                            alt={offerDisplay.unit || 'Unite'}
                            className="h-6 w-6 object-contain sm:h-7 sm:w-7"
                          />
                        ) : (
                          <span className="text-[10px] font-black uppercase tracking-tight text-slate-500">
                            {(offerDisplay.unit || 'T').slice(0, 2)}
                          </span>
                        )}
                      </span>

                      <div className="flex min-w-0 flex-1 flex-col justify-between">
                        <div className="min-w-0">
                          <p className="break-words text-[15px] font-black leading-[1.15] text-slate-900 sm:text-[15px]">
                            {offerDisplay.primary || offerDisplay.fullLabel}
                          </p>
                          {offerDisplay.unit ? (
                            <p className="mt-1 break-words text-xs font-semibold leading-4 text-slate-500 sm:text-[13px]">
                              {offerDisplay.unit}
                            </p>
                          ) : null}
                        </div>
                        <p className="mt-2 text-sm font-medium leading-5 text-slate-500 sm:text-sm">
                          {formatCurrency(offer.price)}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {fieldErrors.offer ? (
              <p className="mt-3 text-sm text-red-600">{fieldErrors.offer}</p>
            ) : null}

            <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50/60 px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-white p-2 text-blue-600 shadow-sm">
                  <ShieldCheck className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 sm:text-base">Paiement 100% securise</h3>
                  <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm">
                    Vos informations sont protegees et le paiement est securise.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-amber-100 bg-amber-50/70 px-4 py-3">
              <div className="flex items-start gap-3">
                <div className="rounded-xl bg-white p-2 text-amber-600 shadow-sm">
                  <Clock3 className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 sm:text-base">Traitement operateur</h3>
                  <p className="mt-1 text-xs leading-5 text-slate-600 sm:text-sm">
                    Vous pouvez passer vos commandes a tout moment. Le traitement operateur se fait de 9h a 21h,
                    les commandes recues hors plage seront prises en charge des la reprise.
                  </p>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting}
              className="mt-5 inline-flex w-full items-center justify-center rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 px-5 py-3 text-sm font-semibold text-white transition hover:from-blue-700 hover:to-blue-600 disabled:cursor-not-allowed disabled:opacity-60 sm:py-3.5 sm:text-base"
            >
              {submitting
                ? 'Traitement...'
                : `Continuer • ${formatCurrency(selectedOffer?.price || 0)}`}
            </button>

            {currentSubmissionMode === TopupTemplateService.SUBMISSION_MODES.WHATSAPP && template?.whatsappNumber ? (
              <p className="mt-3 text-center text-xs text-slate-400">
                La commande sera enregistree dans le dashboard admin puis ouverte sur WhatsApp.
              </p>
            ) : currentSubmissionMode === TopupTemplateService.SUBMISSION_MODES.DASHBOARD ? (
              <p className="mt-3 text-center text-xs text-slate-400">
                La commande sera visible dans le hub admin Topup pour traitement manuel.
              </p>
            ) : null}
          </div>
        </div>

        <div className="rounded-[28px] border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
          <h3 className="text-xl font-bold text-slate-900">Description du service</h3>
          <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-base">
            {product.description || `Rechargez vos ${product.game || product.name}`}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopupServiceDetail;
